import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { useHinweise } from '../../hinweise/HinweisContext'
import { freigegeben, zeigePdf } from './hilfen'
import { Meldungsformular } from './Meldungsformular'
import {
  ARTEN,
  QUELLEN,
  betriebstagHeute,
  linienText,
  verschiebe,
  zeitraum,
  type Tageslage,
  type Verkehrsmeldung,
} from './typen'

interface Uebersicht {
  zuPruefen: number
  meldungen: Verkehrsmeldung[]
}

const STATUS: Record<Verkehrsmeldung['status'], { text: string; klasse: string }> = {
  ZU_PRUEFEN: { text: 'zu prüfen', klasse: 'orange' },
  AKTIV: { text: 'aktiv', klasse: 'gruen' },
  ZURUECKGEZOGEN: { text: 'zurückgezogen', klasse: 'grau' },
}

export function Verkehrsmeldungen() {
  const { hatRolle } = useAuth()
  const darfPflegen = hatRolle('ADMIN', 'DISPONENT')
  // Über eine Referenz: Der Hinweisdienst reicht bei jedem Neuzeichnen eine neue Funktion
  // herein. Als Abhängigkeit von laden() gäbe das eine Endlosschleife — laden zieht das
  // Zeichen nach, das Zeichen zeichnet neu, laden läuft wieder.
  const { aktualisiere } = useHinweise()
  const aktualisiereRef = useRef(aktualisiere)
  aktualisiereRef.current = aktualisiere

  const [datum, setDatum] = useState(betriebstagHeute())
  const [lage, setLage] = useState<Tageslage | null>(null)
  const [alle, setAlle] = useState<Verkehrsmeldung[]>([])
  const [laedt, setLaedt] = useState(true)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [bearbeitet, setBearbeitet] = useState<Verkehrsmeldung | 'neu' | null>(null)
  const [liestAus, setLiestAus] = useState(false)
  const [nurOffene, setNurOffene] = useState(true)
  const dateiwahl = useRef<HTMLInputElement>(null)

  const laden = useCallback(async () => {
    setLaedt(true)
    try {
      const [l, u] = await Promise.all([
        api.get<Tageslage>(`/api/verkehrsmeldungen/lage?datum=${datum}`),
        api.get<Uebersicht>('/api/verkehrsmeldungen'),
      ])
      setLage(l)
      setAlle(u.meldungen)
      // Das Zeichen am Menüpunkt gleich mitziehen — sonst zählt es noch, was eben geprüft wurde.
      aktualisiereRef.current()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    } finally {
      setLaedt(false)
    }
  }, [datum])

  useEffect(() => {
    void laden()
  }, [laden])

  // Verweise aus den Klärungen springen auf eine Meldung: /verkehrsmeldungen?id=41.
  // Nur einmal — wer das Formular schließt, will die Liste sehen und nicht wieder hinein.
  const [parameter] = useSearchParams()
  const vorgabeId = Number(parameter.get('id')) || null
  const vorgabeErledigt = useRef(false)
  useEffect(() => {
    // An "fertig geladen" und nicht an "Liste nicht leer": Sonst bliebe ein Verweis auf
    // eine Meldung, die es nicht mehr gibt, in einem leeren Bestand stumm.
    if (!vorgabeId || vorgabeErledigt.current || laedt) return
    vorgabeErledigt.current = true
    const m = alle.find((x) => x.id === vorgabeId)
    if (m) setBearbeitet(m)
    else {
      setMeldung({
        art: 'fehler',
        text: 'Die Meldung aus dem Verweis gibt es nicht mehr oder sie ist zurückgezogen.',
      })
    }
  }, [vorgabeId, alle, laedt])

  async function hochladen(datei: File) {
    setMeldung(null)
    setLiestAus(true)
    try {
      const neu = await api.hochladen<Verkehrsmeldung>('/api/verkehrsmeldungen/hochladen', datei)
      await laden()
      // Gleich zur Prüfung öffnen: Dafür wurde hochgeladen.
      setBearbeitet(neu)
      setMeldung({
        art: 'erfolg',
        text: 'Hochgeladen. Der Hausdienst liest das PDF aus — die Felder lassen sich auch gleich selbst ausfüllen.',
      })
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Hochladen fehlgeschlagen' })
    } finally {
      setLiestAus(false)
      if (dateiwahl.current) dateiwahl.current.value = ''
    }
  }

  async function aktion(m: Verkehrsmeldung, was: 'aktivieren' | 'zurueckziehen' | 'loeschen') {
    setMeldung(null)
    try {
      if (was === 'loeschen') {
        if (!confirm(`„${m.titel}“ löschen?`)) return
        await api.loeschen(`/api/verkehrsmeldungen/${m.id}`)
        setMeldung({ art: 'erfolg', text: 'Gelöscht.' })
      } else if (was === 'zurueckziehen') {
        if (!confirm(`„${m.titel}“ zurückziehen? Die Fahrer sehen sie dann nicht mehr.`)) return
        await api.post(`/api/verkehrsmeldungen/${m.id}/zurueckziehen`)
        setMeldung({ art: 'erfolg', text: 'Zurückgezogen.' })
      } else {
        const r = await api.post<{ benachrichtigt: number }>(
          `/api/verkehrsmeldungen/${m.id}/aktivieren`,
        )
        setMeldung({ art: 'erfolg', text: freigegeben(r.benachrichtigt) })
      }
      await laden()
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Fehlgeschlagen' })
    }
  }

  const eingang = alle.filter((m) => m.status === 'ZU_PRUEFEN')
  const liste = nurOffene
    ? alle.filter(
        (m) => m.status === 'AKTIV' && (!m.letzterBetriebstag || m.letzterBetriebstag >= datum),
      )
    : alle

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Verkehrsmeldungen</h1>
          <p>
            Umleitungen, Sperrungen und Veranstaltungen — die Fahrer sehen sie an ihren
            betroffenen Diensten in der App.
          </p>
        </div>
        {darfPflegen && !bearbeitet && (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              ref={dateiwahl}
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => e.target.files?.[0] && void hochladen(e.target.files[0])}
            />
            <button
              className="knopf-schlicht"
              disabled={liestAus}
              onClick={() => dateiwahl.current?.click()}
            >
              {liestAus ? 'Wird hochgeladen …' : 'PDF hochladen'}
            </button>
            <button className="knopf-akzent" onClick={() => setBearbeitet('neu')}>
              Meldung anlegen
            </button>
          </div>
        )}
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      {bearbeitet && (
        <Meldungsformular
          meldung={bearbeitet === 'neu' ? null : bearbeitet}
          onFertig={async (text) => {
            setBearbeitet(null)
            if (text) setMeldung({ art: 'erfolg', text })
            await laden()
          }}
        />
      )}

      {eingang.length > 0 && (
        <div className="karte abschnitt">
          <h2>
            Eingang <span className="etikett orange">{eingang.length} zu prüfen</span>
          </h2>
          <p style={{ color: 'var(--grau-700)', marginTop: 0 }}>
            Automatisch erfasst. Die Fahrer sehen diese Meldungen erst nach der Freigabe.
          </p>
          <div className="tabelle-huelle">
            <table>
              <thead>
                <tr>
                  <th>Meldung</th>
                  <th>Zeitraum</th>
                  <th>Linien</th>
                  <th>Herkunft</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {eingang.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <strong>{m.titel}</strong>
                      {m.auslesung === 'AUSSTEHEND' && (
                        <span className="etikett grau" style={{ marginLeft: 6 }}>
                          wartet auf Hausdienst
                        </span>
                      )}
                      {m.kiHinweis && (
                        <div style={{ color: 'var(--orange-600)', fontSize: '0.82rem' }}>
                          {m.kiHinweis}
                        </div>
                      )}
                    </td>
                    <td>{zeitraum(m)}</td>
                    <td>{linienText(m)}</td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {QUELLEN[m.quelle]}
                      {m.eingangBetreff && (
                        <div style={{ color: 'var(--grau-500)' }}>{m.eingangBetreff}</div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {darfPflegen && (
                        <>
                          <button className="knopf-primaer" onClick={() => setBearbeitet(m)}>
                            Prüfen
                          </button>{' '}
                          <button
                            className="knopf-schlicht"
                            style={{ color: 'var(--rot-500)' }}
                            onClick={() => void aktion(m, 'loeschen')}
                            title="Kein Bezug zum Betrieb — verwerfen"
                          >
                            Verwerfen
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="werkzeugleiste">
        <button className="knopf-schlicht" onClick={() => setDatum(verschiebe(datum, -1))}>
          ‹ Vortag
        </button>
        <input
          type="date"
          value={datum}
          onChange={(e) => e.target.value && setDatum(e.target.value)}
          style={{ maxWidth: 170 }}
        />
        <button className="knopf-schlicht" onClick={() => setDatum(verschiebe(datum, 1))}>
          Folgetag ›
        </button>
        <button className="knopf-schlicht" onClick={() => setDatum(betriebstagHeute())}>
          Heute
        </button>
      </div>

      {laedt && !lage ? (
        <div className="laden">Wird geladen …</div>
      ) : (
        <div className="abschnitt">
          <h2>
            {datum === betriebstagHeute() ? 'Heute' : 'Am ' + datum.split('-').reverse().join('.')}{' '}
            aktiv
          </h2>
          {!lage || lage.aktiv.length === 0 ? (
            <div className="karte">
              <div className="leer">An diesem Betriebstag gilt keine Verkehrsmeldung.</div>
            </div>
          ) : (
            <div className="verkehrslage">
              {lage.aktiv.map(({ meldung: m, kurse }) => (
                <Lagekarte
                  key={m.id}
                  m={m}
                  kurse={kurse}
                  onBearbeiten={darfPflegen ? () => setBearbeitet(m) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="abschnitt">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0 }}>{nurOffene ? 'Laufende Meldungen' : 'Alle Meldungen'}</h2>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.88rem' }}>
            <input
              type="checkbox"
              checked={!nurOffene}
              onChange={(e) => setNurOffene(!e.target.checked)}
            />
            auch abgelaufene und zurückgezogene
          </label>
        </div>
        <div className="tabelle-huelle" style={{ marginTop: 10 }}>
          {liste.length === 0 ? (
            <div className="leer">Keine Meldungen.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Meldung</th>
                  <th>Art</th>
                  <th>Zeitraum</th>
                  <th>Linien</th>
                  <th>Fahrer informiert</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {liste.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <span className={`etikett ${STATUS[m.status].klasse}`}>
                        {STATUS[m.status].text}
                      </span>
                    </td>
                    <td>
                      {m.wichtig && <span className="etikett rot">wichtig</span>}{' '}
                      <strong>{m.titel}</strong>
                      <div style={{ color: 'var(--grau-500)', fontSize: '0.82rem' }}>
                        {[m.herausgeber, m.nummer].filter(Boolean).join(' ')}
                      </div>
                    </td>
                    <td>{ARTEN[m.art]}</td>
                    <td>{zeitraum(m)}</td>
                    <td>{linienText(m)}</td>
                    <td className="zahl">{m.benachrichtigt ?? '–'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {m.dateiname && (
                        <button className="knopf-schlicht" onClick={() => void zeigePdf(m.id)}>
                          PDF
                        </button>
                      )}{' '}
                      {darfPflegen && (
                        <>
                          <button className="knopf-schlicht" onClick={() => setBearbeitet(m)}>
                            Bearbeiten
                          </button>{' '}
                          {m.status === 'AKTIV' ? (
                            <button
                              className="knopf-schlicht"
                              onClick={() => void aktion(m, 'zurueckziehen')}
                            >
                              Zurückziehen
                            </button>
                          ) : (
                            <button
                              className="knopf-schlicht"
                              onClick={() => void aktion(m, 'aktivieren')}
                            >
                              Freigeben
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}

function Lagekarte({
  m,
  kurse,
  onBearbeiten,
}: {
  m: Verkehrsmeldung
  kurse: { kursnummer: string; fahrerName: string | null }[]
  onBearbeiten?: () => void
}) {
  const offen = kurse.filter((k) => !k.fahrerName).length
  return (
    <div className={`karte verkehrslage-karte${m.wichtig ? ' wichtig' : ''}`}>
      <div className="verkehrslage-kopf">
        <span className="etikett navy">{ARTEN[m.art]}</span>
        {m.wichtig && <span className="etikett rot">wichtig</span>}
        <span className="verkehrslage-linien">{linienText(m)}</span>
      </div>
      <h3>{m.titel}</h3>
      <div className="verkehrslage-zeit">{zeitraum(m)}</div>
      <dl className="verkehrslage-angaben">
        {m.gesperrteStrasse && (
          <>
            <dt>Gesperrt</dt>
            <dd>{m.gesperrteStrasse}</dd>
          </>
        )}
        {m.umleitungsstrecke && (
          <>
            <dt>Umleitung</dt>
            <dd>{m.umleitungsstrecke}</dd>
          </>
        )}
        {m.entfallendeHaltestellen && (
          <>
            <dt>Entfällt</dt>
            <dd>{m.entfallendeHaltestellen}</dd>
          </>
        )}
        {m.ersatzhaltestellen && (
          <>
            <dt>Ersatz</dt>
            <dd>{m.ersatzhaltestellen}</dd>
          </>
        )}
        {m.besonderheiten && (
          <>
            <dt>Hinweis</dt>
            <dd>{m.besonderheiten}</dd>
          </>
        )}
      </dl>
      <div className="verkehrslage-kurse">
        <strong>
          {kurse.length === 0
            ? 'Kein Kurs dieses Tages fährt die Linie.'
            : `${kurse.length} ${kurse.length === 1 ? 'Kurs' : 'Kurse'} betroffen${offen ? `, ${offen} unbesetzt` : ''}:`}
        </strong>
        <div className="verkehrslage-chips">
          {kurse.map((k) => (
            <span key={k.kursnummer} className={`kurs-chip${k.fahrerName ? '' : ' offen'}`}>
              <b>{k.kursnummer}</b> {k.fahrerName ?? 'unbesetzt'}
            </span>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        {m.dateiname && (
          <button className="knopf-schlicht" onClick={() => void zeigePdf(m.id)}>
            PDF ansehen
          </button>
        )}
        {onBearbeiten && (
          <button className="knopf-schlicht" onClick={onBearbeiten}>
            Bearbeiten
          </button>
        )}
      </div>
    </div>
  )
}
